import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { EmptyState } from '@/components/EmptyState';
import { Check, Download, FileText, X } from 'lucide-react';
import { formatDate, formatNumber, formatRelativeTime } from '@/lib/locale';
import { apiGet, apiGetPaginated, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

type DeliveryType = 'weekly' | 'monthly' | 'alert' | 'onboarding' | 'test';

type Delivery = {
  id: string;
  type: DeliveryType;
  period?: string;
  period_start?: string;
  period_end?: string;
  recipients_count?: number;
  smtp_status?: 'queued' | 'sent' | 'bounced' | 'failed';
  sent_at?: string;
  pages?: number;
  size_bytes?: number;
};

type TenantSettings = {
  weekly_report_day?: string;
  weekly_enabled?: boolean;
  monthly_enabled?: boolean;
  monthly_report_day_of_month?: number;
  report_timezone?: string;
};

const PER_PAGE = 25;

export function DeliveriesScreen({ slug }: { slug: string }) {
  const [page, setPage] = useState(1);
  const params = useMemo(() => ({ page, per_page: PER_PAGE }), [page]);

  const listQ = useQuery({
    queryKey: ['deliveries', slug, params],
    queryFn: () =>
      apiGetPaginated<Delivery>(`/tenants/${slug}/deliveries${qs(params)}`, { tenant: slug }),
  });

  const tenantQ = useQuery({
    queryKey: ['tenants', slug],
    queryFn: () => apiGet<TenantSettings>(`/tenants/${slug}`, { tenant: slug }),
  });

  const rows = listQ.data?.data ?? [];
  const total = listQ.data?.meta?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const weeklyDay = tenantQ.data?.weekly_report_day ?? 'maandag';

  return (
    <div className="max-w-[1300px] space-y-5 p-5">
      <div>
        <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Verzonden rapporten</h1>
        <p className="mt-0.5 text-[13px] text-slate-500">
          PDF-archief, alert-mails en onboarding-berichten · {formatNumber(total)} totaal
        </p>
      </div>

      <QueryState
        isLoading={listQ.isLoading}
        isError={listQ.isError}
        onRetry={() => listQ.refetch()}
      >
        {rows.length === 0 ? (
          <Card>
            <EmptyState
              title="Nog geen verzendingen"
              body={`Wekelijkse rapporten starten op ${weeklyDay} om 07:00. Maandrapporten op de 1e van de maand.`}
            />
          </Card>
        ) : (
          <Card>
            <CardHeader title="Verzendlog" subtitle={`pagina ${page} van ${totalPages}`} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>Type</Th>
                    <Th>Periode</Th>
                    <Th align="right">Ontvangers</Th>
                    <Th>SMTP-status</Th>
                    <Th>Verzonden</Th>
                    <Th align="right">PDF</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((d) => (
                    <tr key={d.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <TypeBadge type={d.type} />
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[11.5px] text-slate-700">
                        {d.period ??
                          (d.period_start && d.period_end
                            ? `${formatDate(d.period_start)} – ${formatDate(d.period_end)}`
                            : '—')}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {d.recipients_count ?? 0}
                      </td>
                      <td className="px-3 py-2.5">
                        <SmtpStatus status={d.smtp_status} />
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {d.sent_at ? formatRelativeTime(d.sent_at) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <a
                          href={`/api/v1/deliveries/${d.id}/pdf`}
                          aria-label="Download PDF"
                          className="inline-flex items-center gap-1 text-info-700 hover:text-info-600"
                        >
                          <FileText size={12} />
                          <Download size={12} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 text-[11px]">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30"
              >
                Vorige
              </button>
              <span className="text-slate-500">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30"
              >
                Volgende
              </button>
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}

function TypeBadge({ type }: { type: DeliveryType }) {
  const map: Record<DeliveryType, { label: string; bg: string; text: string }> = {
    weekly: { label: 'Weekly', bg: 'bg-slate-100', text: 'text-slate-700' },
    monthly: { label: 'Monthly', bg: 'bg-info-50', text: 'text-info-700' },
    alert: { label: 'Alert', bg: 'bg-red-50', text: 'text-red-700' },
    onboarding: { label: 'Onboarding', bg: 'bg-amber-50', text: 'text-amber-700' },
    test: { label: 'Test', bg: 'bg-slate-100', text: 'text-slate-600' },
  };
  const m = map[type];
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-[10.5px] font-medium ${m.bg} ${m.text}`}
    >
      {m.label}
    </span>
  );
}

function SmtpStatus({ status }: { status?: Delivery['smtp_status'] }) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
        <Check size={11} />
        verzonden
      </span>
    );
  }
  if (status === 'bounced' || status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-700">
        <X size={11} />
        {status}
      </span>
    );
  }
  if (status === 'queued') {
    return <span className="text-[11px] text-amber-700">in wachtrij</span>;
  }
  return <span className="text-[11px] text-slate-400">—</span>;
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}
